import {
  collectionGroup,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  startAfter,
  where
} from "firebase/firestore";
import { useEffect, useRef, useState } from "react";
import {
  loadMoreNotifications,
  setNotifications
} from "../Redux/notifications/actions";
import { database } from "../Services/firebase";

const PAGE_SIZE = 50;

export function useNotifications(props) {
  const listeners = useRef({
    inbox: null,
    archived: null,
    unreadCount: null
  });

  const [notificationState, setNotificationState] = useState({
    inbox: {
      loading: false,
      hasMore: true,
      lastDoc: null
    },
    archived: {
      loading: false,
      hasMore: true,
      lastDoc: null
    },
    unreadCount: 0
  });

  const createNotificationQuery = (archived) => {
    return query(
      collectionGroup(database, "notifications"),
      where("user.uid", "==", props.uid),
      where("archived", "==", archived),
      orderBy("timestamp", "desc"),
      limit(PAGE_SIZE)
    );
  };

  const subscribeToNotifications = (type) => {
    const notificationsRef = createNotificationQuery(type === "archived");

    return onSnapshot(
      notificationsRef,
      (snapshot) => {
        const notifications = {};
        snapshot.forEach((doc) => {
          notifications[doc.id] = {
            ...doc.data(),
            documentId: doc.id
          };
        });

        setNotificationState((prevState) => ({
          ...prevState,
          [type]: {
            ...prevState[type],
            lastDoc:
              snapshot.docs.length > 0
                ? snapshot.docs[snapshot.docs.length - 1]
                : null,
            hasMore: snapshot.docs.length > 0
          }
        }));

        setNotifications({
          [type]: notifications
        });
      },
      (error) => {
        console.error(`Error listening to ${type} notifications:`, error);
      }
    );
  };

  const loadMore = async (type) => {
    const { loading, hasMore, lastDoc } = notificationState[type];

    if (!hasMore || loading || !lastDoc) return;

    setNotificationState((prevState) => ({
      ...prevState,
      [type]: {
        ...prevState[type],
        loading: true
      }
    }));

    try {
      const notificationsRef = query(
        collectionGroup(database, "notifications"),
        where("user.uid", "==", props.uid),
        where("archived", "==", type === "archived"),
        orderBy("timestamp", "desc"),
        startAfter(lastDoc),
        limit(PAGE_SIZE)
      );

      const snapshot = await getDocs(notificationsRef);
      const moreNotifications = {};

      snapshot.forEach((doc) => {
        moreNotifications[doc.id] = {
          ...doc.data(),
          documentId: doc.id
        };
      });

      const hasMoreNotifications = snapshot.docs.length > 0;

      setNotificationState((prevState) => ({
        ...prevState,
        [type]: {
          ...prevState[type],
          lastDoc: hasMoreNotifications
            ? snapshot.docs[snapshot.docs.length - 1]
            : prevState[type].lastDoc,
          hasMore: hasMoreNotifications,
          loading: false
        }
      }));

      if (hasMoreNotifications) {
        loadMoreNotifications({
          type,
          notifications: moreNotifications
        });
      }
    } catch (error) {
      console.error(`Error loading more ${type} notifications:`, error);

      setNotificationState((prevState) => ({
        ...prevState,
        [type]: {
          ...prevState[type],
          loading: false
        }
      }));
    }
  };

  useEffect(() => {
    if (props.isAuth && props.uid) {
      if (!listeners.current.inbox) {
        listeners.current.inbox = subscribeToNotifications("inbox");
      }

      if (!listeners.current.archived) {
        listeners.current.archived = subscribeToNotifications("archived");
      }
    } else {
      ["inbox", "archived"].forEach((type) => {
        if (typeof listeners.current[type] === "function") {
          listeners.current[type]();
          listeners.current[type] = null;
        }
      });
    }

    // eslint-disable-next-line
  }, [props.isAuth, props.uid]);

  return {
    notification: {
      notificationState,
      loadMoreNotifications: (type) => loadMore(type)
    }
  };
}
